import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  ReactNode,
} from "react";
import { showToast } from "../lib/toast";
import { useNavigate, useLocation } from "react-router-dom";
import Modal from "../components/ui/Modal";
import Button from "../components/ui/Button";
import { User } from "../lib/types";
import { AuthAPI } from "../lib/api/auth";
import { MfaAPI } from "../lib/api/mfa";
import { UserAPI } from "../lib/api/user";

/**
 * "mfa_required" means the password was accepted but no session exists yet —
 * the caller must collect a second factor and call completeMfaLogin.
 */
type LoginOutcome = "success" | "mfa_required" | "failed";

interface AuthContextType {
  user: User | null;
  session: any | null;
  loading: boolean;
  mfaPending: boolean;
  login: (email: string, password: string) => Promise<LoginOutcome>;
  loginWithGoogle: (code: string, codeVerifier: string) => Promise<"success" | "mfa_required">;
  completeMfaLogin: (code: string) => Promise<void>;
  cancelMfaLogin: () => void;
  logout: () => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  verifyResetToken: (tokenHash: string) => Promise<boolean>;
  updatePassword: (password: string) => Promise<boolean>;
  refreshUser: () => Promise<void>;
  acceptTos: (version: string) => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [mfaPending, setMfaPending] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  // Tracks whether a sign-out was triggered intentionally (vs. session expiry)
  const isIntentionalLogout = useRef(false);
  // Mirrors user state so the auth listener can check it without stale closure
  const userRef = useRef<User | null>(null);

  const refreshUser = async () => {
    try {
      const userData = await UserAPI.me();
      setUser(userData);
      userRef.current = userData;
      setSessionExpired(false);
    } catch (error) {
      // If we had a user previously and it fails without explicit intent, session ran out
      if (!isIntentionalLogout.current && userRef.current !== null) {
        setSessionExpired(true);
      }
      setUser(null);
      userRef.current = null;
    }
  };

  // Check for existing session on mount and re-verify on route changes
  useEffect(() => {
    let mounted = true;
    const verifyAuth = async () => {
      // Don't block UI with loading spinner on every path change if we already have a user
      if (userRef.current === null && !isIntentionalLogout.current) {
        setLoading(true);
      }
      await refreshUser();
      
      if (mounted) setLoading(false);
    };
    
    verifyAuth();
    
    return () => { mounted = false; };
  }, [location.pathname]);

  // Cross-tab synchronization: Listen for logout events from other tabs
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      // Listen for our specific manual logout flag
      if (e.key === "vein_logout_timestamp") {
        // Since the logout happened in *another* tab, we treat it as an expired session here
        isIntentionalLogout.current = false;
        setUser(null);
        setSession(null);
        userRef.current = null;
        setSessionExpired(true);
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  // Neither login call touches `loading` — App unmounts the login page while
  // it's set, which would discard the pending-MFA state mid-call. The forms
  // own their own submitting state instead.
  const login = async (email: string, password: string): Promise<LoginOutcome> => {
    isIntentionalLogout.current = false;

    try {
      const result = await AuthAPI.login(email, password);
      if (result.mfaRequired) {
        setMfaPending(true);
        return "mfa_required";
      }
      setMfaPending(false);
      await refreshUser();
      showToast.success("Logged in successfully!");
      return "success";
    } catch (error) {
      // Login failed
      showToast.error(error, "We couldn't log you in. Please try again.");
      return "failed";
    }
  };

  // Same mfa_required branching as login(); errors propagate to OAuthCallbackPage.
  const loginWithGoogle = async (
    code: string,
    codeVerifier: string,
  ): Promise<"success" | "mfa_required"> => {
    isIntentionalLogout.current = false;

    const result = await AuthAPI.oauthCallback(code, codeVerifier);
    if (result.mfaRequired) {
      setMfaPending(true);
      return "mfa_required";
    }
    setMfaPending(false);
    await refreshUser();
    showToast.success("Logged in successfully!");
    return "success";
  };

  // Errors propagate so the code entry form can show them against the input.
  const completeMfaLogin = async (code: string): Promise<void> => {
    await MfaAPI.verifyLogin(code);
    setMfaPending(false);
    await refreshUser();
    showToast.success("Logged in successfully!");
  };

  const cancelMfaLogin = () => setMfaPending(false);

  const logout = async () => {
    isIntentionalLogout.current = true;
    setUser(null);
    setSession(null);
    userRef.current = null;
    
    try {
      await AuthAPI.logout();
      // Trigger the storage event in other tabs
      window.localStorage.setItem("vein_logout_timestamp", Date.now().toString());
      showToast.success("Logged out");
    } catch (err) {
      console.error("Logout error", err);
    }
  };

  const forgotPassword = async (email: string) => {
    try {
      await AuthAPI.forgotPassword(email);
      showToast.success("Reset link sent! Check your inbox.");
    } catch (error) {
      console.error("Forgot password request failed", error);
      showToast.errorMessage("We couldn't send the reset email right now. Please try again.");
    }
  };

  const verifyResetToken = async (tokenHash: string): Promise<boolean> => {
    try {
      await AuthAPI.verifyResetToken(tokenHash);
      return true;
    } catch {
      return false;
    }
  }

  const updatePassword = async (password: string): Promise<boolean> => {
    try {
      await AuthAPI.updatePassword(password);
      return true;
    } catch {
      return false;
    }
  }

  const acceptTos = async (version: string): Promise<boolean> => {
    try {
      await UserAPI.acceptTos(version);
      await refreshUser();
      return true;
    } catch (error) {
      showToast.error(error, "We couldn't record your agreement. Please try again.");
      return false;
    }
  }

  const value: AuthContextType = {
    user,
    session,
    loading,
    mfaPending,
    login,
    loginWithGoogle,
    completeMfaLogin,
    cancelMfaLogin,
    logout,
    forgotPassword,
    verifyResetToken,
    updatePassword,
    refreshUser,
    acceptTos,
  };

  const handleGoToLogin = () => {
    setSessionExpired(false);
    navigate("/login");
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      <Modal
        isOpen={sessionExpired}
        onClose={() => {}}
        closeOnBackdrop={false}
        closeOnEscape={false}
        hideCloseButton
        size="sm"
        title="Session Expired"
        footer={
          <Button fullWidth autoFocus onClick={handleGoToLogin}>
            Go to Login
          </Button>
        }
      >
        <div className="flex flex-col items-center text-center gap-4">
          <div className="w-14 h-14 rounded-xl bg-red-50 flex items-center justify-center">
            <svg
              className="w-7 h-7 text-red-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.8}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v3.75m0 3.75h.008v.008H12v-.008zm9-3.75A9 9 0 1 1 3 12a9 9 0 0 1 18 0z"
              />
            </svg>
          </div>
          <p className="text-sm text-gray-500 leading-relaxed">
            Your session has timed out. Please log in again to continue.
          </p>
        </div>
      </Modal>
    </AuthContext.Provider>
  );
}